import { type ButtonProps, type ButtonSize, type ButtonVariant } from "ui/button";

export type JsonFormOptionValue = string | number | boolean;

export type JsonFormUiType =
  | "text"
  | "date"
  | "textarea"
  | "select"
  | "select-search"
  | "checkbox"
  | "toggle"
  | "checkbox-group"
  | "multi-select"
  | "radio-group"
  | "dynamic-section"
  | "number"
  | "button"
  | "upload";

export type JsonFormDynamicSectionConfig = {
  itemLabel?: string;
  itemLabelField?: string;
  itemLabelOptions?: Record<string, string>;
  /** Read-only: field shown under the item title (e.g. email type id). */
  itemSubtitleField?: string;
  /** Read-only: prefix before subtitle value (e.g. "ID "). */
  itemSubtitlePrefix?: string;
  /** Read-only: boolean field rendered as a disabled toggle in the card header. */
  headerToggleField?: string;
  /** Read-only: boolean field shown as a badge next to the item title. */
  badgeField?: string;
  badgeLabel?: string;
  /**
   * Read-only item numbering: "hash" → "Label #1" (default), "suffix" → "Label 1".
   */
  itemNumberFormat?: "hash" | "suffix";
  /** Read-only: hide per-item title when there is only one item. */
  hideSingleItemLabel?: boolean;
  /** Read-only: show Copy text action (default true). */
  showCopyText?: boolean;
  /** Read-only: whether items collapse (default true). */
  collapsible?: boolean;
  /** Start items collapsed (default false). */
  defaultCollapsed?: boolean;
  /**
   * Read-only layout:
   * - auto: flat table when all item fields are scalars, else cards
   * - cards: bordered expandable cards
   * - stacked: plain headings + fields (nested sender lists)
   */
  readOnlyLayout?: "auto" | "cards" | "stacked";
  /** Read-only empty list message for this dynamic section. */
  emptyMessage?: string;
  addButtonLabel?: string;
  deleteButtonLabel?: string;
  rowGroups?: string[][];
  minItems?: number;
  /**
   * Suppress all per-item chrome — outer border, collapsible header/chevron/item-label row, and the
   * Add/Delete buttons — regardless of item count. Rows still render, stacked with a gap, for sections
   * whose item already carries its own label inline (e.g. a disabled `name` field) so the card chrome
   * is redundant. Edit-mode (`FormBuilderDynamicSection`) always honors this; read-only
   * (`FormBuilderViewerDynamicSection`) does too, unless `readOnlyLayout` is explicitly set to
   * `"cards"` or `"stacked"` — an explicit read-only layout choice wins there.
   */
  chromeless?: boolean;
  /**
   * Field name to fill with a generated id (`crypto.randomUUID()`) when a row is appended via
   * the Add button. For sections whose rows are derived from — and need to be reconciled
   * against — data outside the array itself (e.g. Channels' per-creative SMS rows), so a stable
   * id exists from the moment a row is added, not only after save.
   */
  generateIdField?: string;
};

export type JsonFormSchemaType =
  | "string"
  | "number"
  | "integer"
  | "boolean"
  | "object"
  | "array";

export type JsonFormUploadConfig = {
  /** `accept` attribute for the file input, e.g. ".svg,image/svg+xml". */
  accept?: string;
  /** Display label for the "Format:" row, e.g. "SVG". */
  acceptLabel?: string;
  maxSizeKb?: number;
  /** Preview swatch background, matching the target logo's intended use. */
  previewBackground?: "light" | "dark";
  /**
   * Render the shared "Format: X / Maximum file size: Y KB" block once,
   * right after this field. Set on whichever upload field in a group should
   * carry it (typically the last one), since the block describes the whole
   * group rather than any single field.
   */
  showRequirements?: boolean;
};

export type JsonFormOption = {
  label: string;
  value: JsonFormOptionValue;
};

export type JsonFormSection = {
  id: string;
  label: string;
  property: string;
  contentTitle?: string;
  children?: JsonFormSection[];
};

export type JsonFormSectionAction = {
  id: string;
  label: string;
  color?: ButtonProps["color"];
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Hide this action when the named field (in the tab's own schema) is empty. */
  hideWhenFieldEmpty?: string;
};

export type FormSchemaSidebarItem = {
  id: string;
  label: string;
  children?: FormSchemaSidebarItem[];
};

export type JsonFormSectionHeadingLevel = "section" | "subsection" | "field-group" | "heading-sm";

export type JsonFormSchema = {
  type?: JsonFormSchemaType;
  title?: string;
  description?: string;
  default?: unknown;
  enum?: JsonFormOptionValue[];
  const?: JsonFormOptionValue;
  properties?: Record<string, JsonFormSchema>;
  required?: string[];
  additionalProperties?: boolean | JsonFormSchema;
  items?: JsonFormSchema;
  uniqueItems?: boolean;
  allOf?: JsonFormSchema[];
  if?: JsonFormSchema;
  then?: JsonFormSchema;
  contains?: JsonFormSchema;
  $ref?: string;
  $defs?: Record<string, JsonFormSchema>;
  format?: string;
  "x-ui-type"?: JsonFormUiType;
  "x-placeholder"?: string;
  "x-order"?: number;
  "x-options"?: JsonFormOption[];
  "x-error-message"?: string;
  "x-mask"?: string;
  "x-heading-level"?: JsonFormSectionHeadingLevel;
  "x-row-groups"?: string[][];
  /**
   * `[start, end]` sibling `date` fields edited through one `ui/range-picker`. Each stays its own value,
   * so validation, prefill and read-only still see two fields; list the pair in `x-row-groups` too so
   * read-only keeps them side by side.
   */
  "x-date-ranges"?: [string, string][];
  "x-dynamic-section"?: JsonFormDynamicSectionConfig;
  "x-sections"?: JsonFormSection[];
  "x-disabled-options"?: JsonFormOptionValue[];
  /** Field renders read-only (e.g. a system-assigned value shown for reference only). */
  "x-disabled"?: boolean;
  /** Field is needed to generate the campaign with the AI writer — drives an info icon + tooltip on the field, and a plain icon at the section level when the field is empty. */
  "x-needed-for-generation"?: boolean;
  /** Select/select-search field shows a clear icon to unset the current value. */
  "x-clearable"?: boolean;
  "x-section-info"?: string;
  "x-section-layout"?: "compact" | "default";
  /** Actions rendered next to the section/tab title (e.g. Sync). */
  "x-section-actions"?: JsonFormSectionAction[];
  /** Action id resolved by the page via FormBuilder `actions` map. */
  "x-action"?: string;
  "x-button-color"?: ButtonProps["color"];
  "x-button-variant"?: ButtonVariant;
  "x-button-icon"?: "trash";
  /**
   * Field has no editable control anywhere in edit mode (e.g. populated only
   * by an external sync). Distinct from being an `itemLabelField` — a field
   * can be the item label AND still need a real editable input in the body
   * (e.g. a2a-program `topics.class`, a select the user picks and which also
   * derives the header label). Only set this when the field truly has no
   * edit-mode input anywhere.
   */
  "x-hidden-in-edit"?: boolean;
  "x-upload"?: JsonFormUploadConfig;
  /** Read-only: render this scalar field's value as a clickable link. */
  "x-display-as-link"?: boolean;
  /**
   * Conditional-dependent field only: instead of nesting inline inside its trigger field's own grid
   * cell (the default), render as its own full-width row directly below the row-group grid that
   * contains the trigger, still gated by the same show-if condition.
   */
  "x-full-width"?: boolean;

  // Temporary aliases used only while local fixtures emulate the BE response.
  label?: string;
  placeholder?: string;
  uiType?: JsonFormUiType;
  options?: JsonFormOption[];
  dependencies?: Record<
    string,
    {
      showIf: JsonFormOptionValue;
      fields: Record<string, JsonFormSchema>;
    }
  >;
};

export type JsonFormObjectSchema = JsonFormSchema & {
  type: "object";
  properties: Record<string, JsonFormSchema>;
  $defs?: Record<string, JsonFormSchema>;
};

export type JsonFormConditionalRule = {
  triggerFieldName: string;
  showIf: JsonFormOptionValue;
  dependentFieldNames: string[];
  requiredFieldNames: string[];
};

const getConditionalRuleFromSchema = (schema: JsonFormSchema): JsonFormConditionalRule | undefined => {
  const conditionProperties = schema.if?.properties;
  const conditionEntry = conditionProperties
    ? Object.entries(conditionProperties).find(
        ([, condition]) =>
          condition.contains?.const !== undefined || condition.const !== undefined,
      )
    : undefined;

  if (!conditionEntry) {
    return undefined;
  }

  const [triggerFieldName, condition] = conditionEntry;
  const requiredFieldNames = [...(schema.then?.required ?? [])];
  const dependentFieldNames = [
    ...requiredFieldNames,
    ...Object.keys(schema.then?.properties ?? {}),
  ].filter((value, index, values) => values.indexOf(value) === index);
  const showIf = condition.contains?.const ?? condition.const;

  if (showIf === undefined || dependentFieldNames.length === 0) {
    return undefined;
  }

  return {
    triggerFieldName,
    showIf,
    dependentFieldNames,
    requiredFieldNames,
  };
};

export const getConditionalRules = (schema: JsonFormObjectSchema): JsonFormConditionalRule[] => {
  const rules = [schema, ...(schema.allOf ?? [])].flatMap((subSchema) => {
    const rule = getConditionalRuleFromSchema(subSchema);

    return rule ? [rule] : [];
  });

  const merged = new Map<string, JsonFormConditionalRule>();

  for (const rule of rules) {
    const key = `${rule.triggerFieldName}::${String(rule.showIf)}`;
    const existing = merged.get(key);

    if (existing) {
      merged.set(key, {
        ...existing,
        dependentFieldNames: [
          ...new Set([...existing.dependentFieldNames, ...rule.dependentFieldNames]),
        ],
        requiredFieldNames: [
          ...new Set([...existing.requiredFieldNames, ...rule.requiredFieldNames]),
        ],
      });
    } else {
      merged.set(key, rule);
    }
  }

  return Array.from(merged.values());
};

export const getConditionalDependentFieldNames = (schema: JsonFormObjectSchema): Set<string> =>
  new Set(getConditionalRules(schema).flatMap((rule) => rule.dependentFieldNames));

/** Whether a trigger field's current value satisfies a conditional rule's `showIf`. */
export const matchesConditionalShowIf = (
  value: unknown,
  showIf: JsonFormOptionValue,
): boolean => (Array.isArray(value) ? value.includes(showIf) : value === showIf);

export const getConditionalRequiredFieldNames = (schema: JsonFormObjectSchema): Set<string> =>
  new Set(getConditionalRules(schema).flatMap((rule) => rule.requiredFieldNames));

export type DynamicFormSchema = JsonFormObjectSchema;

export type FormPageSchemaTab = {
  id: string;
  label: string;
  schema: JsonFormObjectSchema;
  dataPath?: string[];
  sidebarLabel?: string;
  parentSectionId?: string;
};

const isObjectSchema = (schema: JsonFormSchema): schema is JsonFormObjectSchema =>
  schema.type === "object" && !!schema.properties;

const optionValues = (options: JsonFormOption[] = []): JsonFormOptionValue[] =>
  options.map((option) => option.value);

export const sortJsonFormFields = (
  entries: [string, JsonFormSchema][],
): [string, JsonFormSchema][] =>
  entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const aOrder = a.entry[1]["x-order"] ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.entry[1]["x-order"] ?? Number.MAX_SAFE_INTEGER;

      return aOrder === bOrder ? a.index - b.index : aOrder - bOrder;
    })
    .map(({ entry }) => entry);

export const getRowGroups = (schema: JsonFormSchema): string[][] => schema["x-row-groups"] ?? [];

export const getDateRanges = (schema: JsonFormSchema): [string, string][] =>
  schema["x-date-ranges"] ?? [];

/**
 * A range that is its object's first field takes the object's title as its group label, in place of the
 * section heading: Figma's date-range field sets a bold group label over regular sub-labels, which a
 * heading above the range would duplicate in the wrong style.
 */
export const leadsWithDateRange = (schema: JsonFormSchema): boolean => {
  const [firstEntry] = sortJsonFormFields(Object.entries(schema.properties ?? {}));

  return !!firstEntry && getDateRanges(schema).some(([startName]) => startName === firstEntry[0]);
};

export const getDynamicSectionConfig = (field: JsonFormSchema): Required<
    Pick<
      JsonFormDynamicSectionConfig,
      | "itemLabel"
      | "addButtonLabel"
      | "deleteButtonLabel"
      | "minItems"
      | "itemNumberFormat"
      | "showCopyText"
      | "collapsible"
      | "defaultCollapsed"
      | "readOnlyLayout"
      | "hideSingleItemLabel"
    >
  > &
  JsonFormDynamicSectionConfig => ({
  itemLabel: "Item",
  addButtonLabel: "Add item",
  deleteButtonLabel: "Delete",
  minItems: 1,
  itemNumberFormat: "hash",
  showCopyText: true,
  collapsible: true,
  defaultCollapsed: false,
  readOnlyLayout: "auto",
  hideSingleItemLabel: false,
  ...field["x-dynamic-section"],
});

export const resolveDynamicSectionItemLabel = (
  row: Record<string, unknown>,
  index: number,
  field: JsonFormSchema,
): string => {
  const config = getDynamicSectionConfig(field);

  if (config.itemLabelField) {
    const fieldValue = row[config.itemLabelField];

    if (typeof fieldValue === "string" && fieldValue.trim()) {
      if (config.itemLabelOptions) {
        const labelPrefix = config.itemLabelOptions[fieldValue];

        if (labelPrefix) {
          return config.itemNumberFormat === "suffix"
            ? `${labelPrefix} ${index + 1}`
            : `${labelPrefix} #${index + 1}`;
        }
      } else {
        return fieldValue;
      }
    }
  }

  return config.itemNumberFormat === "suffix"
    ? `${config.itemLabel} ${index + 1}`
    : `${config.itemLabel} #${index + 1}`;
};

export const getDynamicSectionHeaderFieldNames = (field: JsonFormSchema): Set<string> => {
  const config = getDynamicSectionConfig(field);

  return new Set(
    [config.itemLabelField, config.itemSubtitleField, config.headerToggleField, config.badgeField].filter(
      (name): name is string => Boolean(name),
    ),
  );
};

/**
 * Edit-mode equivalent of `getDynamicSectionHeaderFieldNames`, deliberately
 * narrower: `itemLabelField` is NOT included here. Read-only mode always
 * hides it because the header already shows its value as static text, but in
 * edit mode `itemLabelField` frequently names a field the user still edits
 * directly (e.g. a2a-program `topics.class`, a select whose value both
 * derives the header label and needs its own input). Only fields whose
 * *entire* edit-mode representation lives in the header — the interactive
 * `headerToggleField` toggle, the read-only subtitle text, the (currently
 * unrendered) badge field — are safe to drop from the body universally.
 * A field named by `itemLabelField` that truly has no separate edit input
 * (e.g. college-profile `emailTypeName`, populated only by sync) should be
 * marked `x-hidden-in-edit` on the field itself instead.
 */
export const getDynamicSectionEditHeaderOnlyFieldNames = (field: JsonFormSchema): Set<string> => {
  const config = getDynamicSectionConfig(field);

  return new Set(
    [config.itemSubtitleField, config.headerToggleField, config.badgeField].filter(
      (name): name is string => Boolean(name),
    ),
  );
};

export const isFieldHiddenInEdit = (field: JsonFormSchema): boolean =>
  Boolean(field["x-hidden-in-edit"]);

export const isFieldDisabled = (field: JsonFormSchema): boolean => Boolean(field["x-disabled"]);

export const isFieldClearable = (field: JsonFormSchema): boolean => Boolean(field["x-clearable"]);

export const isFullWidthField = (field: JsonFormSchema | undefined): boolean =>
  Boolean(field?.["x-full-width"]);

export const isActionButtonField = (field: JsonFormSchema): boolean =>
  (field["x-ui-type"] ?? field.uiType) === "button";

export const isUploadField = (field: JsonFormSchema): boolean =>
  (field["x-ui-type"] ?? field.uiType) === "upload";

export const getUploadConfig = (field: JsonFormSchema): JsonFormUploadConfig => ({
  previewBackground: "light",
  ...field["x-upload"],
});

export const isDisplayedAsLink = (field: JsonFormSchema): boolean =>
  Boolean(field["x-display-as-link"]);

export const getFieldAction = (field: JsonFormSchema): string | undefined => field["x-action"];

export const getFieldButtonColor = (field: JsonFormSchema): ButtonProps["color"] =>
  field["x-button-color"];

export const getFieldButtonVariant = (field: JsonFormSchema): ButtonVariant =>
  field["x-button-variant"] ?? "outlined";

export const getFieldButtonIcon = (
  field: JsonFormSchema,
): JsonFormSchema["x-button-icon"] | undefined => field["x-button-icon"];

export const deriveFieldDefaults = (
  properties: Record<string, JsonFormSchema>,
): Record<string, unknown> => {
  const defaults: Record<string, unknown> = {};

  for (const [key, field] of Object.entries(properties)) {
    if (isActionButtonField(field)) {
      continue;
    }

    if ("default" in field) {
      defaults[key] = field.default;
      continue;
    }

    switch (field.type) {
      case "array":
        if (field["x-ui-type"] === "dynamic-section" && field.items?.properties) {
          const minItems = getDynamicSectionConfig(field).minItems;
          const itemDefault = deriveFieldDefaults(field.items.properties);

          defaults[key] = Array.from({ length: minItems }, () => ({ ...itemDefault }));
        } else {
          defaults[key] = [];
        }
        break;
      case "boolean":
        defaults[key] = false;
        break;
      case "object":
        if (field.properties) {
          defaults[key] = deriveFieldDefaults(field.properties);
        }
        break;
      case "integer":
      case "number":
      case "string":
      default:
        defaults[key] = undefined;
        break;
    }
  }

  return defaults;
};

export const deriveDefaultValues = (schema: JsonFormObjectSchema): Record<string, unknown> =>
  deriveFieldDefaults(normalizeJsonFormSchema(schema).properties);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const mergeFieldDefaults = (
  field: JsonFormSchema,
  derived: unknown,
  provided: unknown,
): unknown => {
  if (field.type === "array" && field["x-ui-type"] === "dynamic-section") {
    const minItems = getDynamicSectionConfig(field).minItems;

    if (!Array.isArray(provided) || provided.length < minItems) {
      return derived;
    }

    if (field.items?.properties) {
      const itemDefault = deriveFieldDefaults(field.items.properties);

      return provided.map((item) =>
        isPlainObject(item) ? { ...itemDefault, ...item } : item,
      );
    }

    return provided;
  }

  if (field.type === "object" && field.properties && isPlainObject(derived)) {
    return mergeObjectFieldDefaults(
      field as JsonFormObjectSchema,
      derived,
      isPlainObject(provided) ? provided : {},
    );
  }

  return provided !== undefined ? provided : derived;
};

const mergeObjectFieldDefaults = (
  objectSchema: JsonFormObjectSchema,
  derived: Record<string, unknown>,
  provided: Record<string, unknown>,
): Record<string, unknown> => {
  const merged: Record<string, unknown> = { ...derived };

  for (const [key, field] of Object.entries(objectSchema.properties)) {
    merged[key] = mergeFieldDefaults(field, derived[key], provided[key]);
  }

  return merged;
};

export const mergeDefaultValues = (
  schema: JsonFormObjectSchema,
  defaultValues?: Record<string, unknown>,
): Record<string, unknown> => {
  const normalizedSchema = normalizeJsonFormSchema(schema);
  const derived = deriveDefaultValues(normalizedSchema);

  if (!defaultValues) {
    return derived;
  }

  return mergeObjectFieldDefaults(normalizedSchema, derived, defaultValues);
};

const resolveSchemaRef = (
  field: JsonFormSchema,
  defs?: Record<string, JsonFormSchema>,
): JsonFormSchema => {
  if (!field.$ref?.startsWith("#/$defs/") || !defs) {
    return field;
  }

  const defName = field.$ref.slice("#/$defs/".length);
  const resolved = defs[defName];

  return resolved ? resolveSchemaRef(resolved, defs) : field;
};

const normalizeFieldSchema = (
  field: JsonFormSchema,
  defs?: Record<string, JsonFormSchema>,
): JsonFormSchema => {
  const resolvedField = resolveSchemaRef(field, defs);
  // Destructure legacy aliases out so they are never present in the output.
  const {
    label,
    uiType,
    placeholder,
    options,
    dependencies: _dependencies,
    $ref: _ref,
    ...rest
  } = resolvedField;

  const title = rest.title ?? label;
  const resolvedUiType = rest["x-ui-type"] ?? uiType;
  const resolvedPlaceholder = rest["x-placeholder"] ?? placeholder;
  const resolvedOptions = rest["x-options"] ?? options;
  const normalizedItems =
    rest.items?.type === "object" && rest.items.properties
      ? ({
          ...rest.items,
          type: "object" as const,
          additionalProperties: rest.items.additionalProperties ?? false,
          properties: Object.fromEntries(
            Object.entries(rest.items.properties).map(([key, childField]) => [
              key,
              normalizeFieldSchema(childField, defs),
            ]),
          ),
        } satisfies JsonFormObjectSchema)
      : rest.items
        ? normalizeFieldSchema(rest.items, defs)
        : undefined;
  const properties = rest.properties
    ? Object.fromEntries(
        Object.entries(rest.properties).map(([key, childField]) => [
          key,
          normalizeFieldSchema(childField, defs),
        ]),
      )
    : undefined;

  return {
    ...rest,
    ...(title ? { title } : {}),
    ...(resolvedUiType ? { "x-ui-type": resolvedUiType } : {}),
    ...(resolvedPlaceholder ? { "x-placeholder": resolvedPlaceholder } : {}),
    ...(resolvedOptions ? { "x-options": resolvedOptions } : {}),
    ...(properties ? { properties } : {}),
    ...(rest.type === "object"
      ? { additionalProperties: rest.additionalProperties ?? false }
      : {}),
    ...(rest.type === "array"
      ? resolvedUiType === "dynamic-section"
        ? {
            items: normalizedItems ?? {
              type: "object",
              additionalProperties: false,
              properties: {},
            },
          }
        : {
            uniqueItems: rest.uniqueItems ?? true,
            items: normalizedItems ?? {
              type: "string",
              ...(resolvedOptions ? { enum: optionValues(resolvedOptions) } : {}),
            },
          }
      : {}),
  };
};

const dependencyToConditional = ([triggerFieldName, field]: [
  string,
  JsonFormSchema,
]): JsonFormSchema[] => {
  if (!field.dependencies) {
    return [];
  }

  return Object.values(field.dependencies).map((dependency) => {
    const dependentProperties = Object.fromEntries(
      Object.entries(dependency.fields).map(([key, dependentField]) => [
        key,
        normalizeFieldSchema(dependentField),
      ]),
    );

    // Object.fromEntries prevents "then" from appearing as a literal property key
    // (JSON Schema keyword — not a Promise thenable).
    return Object.fromEntries([
      [
        "if",
        {
          properties: {
            [triggerFieldName]:
              field.type === "array"
                ? { contains: { const: dependency.showIf } }
                : { const: dependency.showIf },
          },
        },
      ],
      ["then", { required: Object.keys(dependentProperties) }],
    ]) as JsonFormSchema;
  });
};

const normalizedSchemaCache = new WeakMap<JsonFormObjectSchema, JsonFormObjectSchema>();

export const normalizeJsonFormSchema = (
  schema: JsonFormObjectSchema,
): JsonFormObjectSchema => {
  const cached = normalizedSchemaCache.get(schema);

  if (cached) {
    return cached;
  }

  // Destructure legacy aliases out so they are never present in the output.
  const {
    label: _label,
    uiType: _uiType,
    placeholder: _placeholder,
    options: _options,
    dependencies: _dependencies,
    ...rest
  } = schema;
  const defs = schema.$defs;

  const properties = Object.fromEntries(
    Object.entries(schema.properties).map(([key, field]) => [
      key,
      normalizeFieldSchema(field, defs),
    ]),
  );

  const dependencyProperties = Object.fromEntries(
    Object.values(schema.properties).flatMap((field) =>
      Object.values(field.dependencies ?? {}).flatMap((dependency) =>
        Object.entries(dependency.fields).map(([key, dependentField]) => [
          key,
          normalizeFieldSchema(dependentField, defs),
        ]),
      ),
    ),
  );

  const dependencyConditionals = Object.entries(schema.properties).flatMap(
    dependencyToConditional,
  );

  const normalized: JsonFormObjectSchema = {
    ...rest,
    type: "object",
    ...(defs ? { $defs: defs } : {}),
    properties: {
      ...properties,
      ...dependencyProperties,
    },
    additionalProperties: schema.additionalProperties ?? false,
    ...(dependencyConditionals.length > 0
      ? { allOf: [...(schema.allOf ?? []), ...dependencyConditionals] }
      : {}),
  };

  normalizedSchemaCache.set(schema, normalized);

  return normalized;
};

export const createFormSchemaFromSections = (
  sections: FormPageSchemaTab[],
): JsonFormObjectSchema => ({
  type: "object",
  additionalProperties: false,
  properties: Object.fromEntries(
    sections.map((section) => [section.id, normalizeJsonFormSchema(section.schema)]),
  ),
  "x-sections": sections.map((section) => ({
    id: section.id,
    label: section.label,
    property: section.id,
  })),
});

export const getNestedValue = (data: Record<string, unknown>, path: string[]): unknown =>
  path.reduce<unknown>((current, key) => {
    if (!isPlainObject(current)) {
      return undefined;
    }

    return current[key];
  }, data);

export const mergeTabFormData = (
  existing: Record<string, unknown>,
  submitted: Record<string, unknown>,
): Record<string, unknown> => {
  const merged: Record<string, unknown> = { ...existing };

  for (const [key, submittedValue] of Object.entries(submitted)) {
    if (submittedValue === undefined) {
      continue;
    }

    const existingValue = merged[key];

    if (Array.isArray(submittedValue)) {
      merged[key] = submittedValue;
      continue;
    }

    if (isPlainObject(submittedValue) && isPlainObject(existingValue)) {
      merged[key] = mergeTabFormData(existingValue, submittedValue);
      continue;
    }

    merged[key] = submittedValue;
  }

  return merged;
};

export const setNestedValue = (
  data: Record<string, unknown>,
  path: string[],
  value: unknown,
): Record<string, unknown> => {
  if (path.length === 0) {
    return data;
  }

  const [head, ...rest] = path;

  if (rest.length === 0) {
    return { ...data, [head]: value };
  }

  const current = data[head];

  return {
    ...data,
    [head]: setNestedValue(isPlainObject(current) ? current : {}, rest, value),
  };
};

const attachRootSchemaDefs = (
  sectionSchema: JsonFormObjectSchema,
  rootSchema: JsonFormObjectSchema,
): JsonFormObjectSchema => {
  if (!rootSchema.$defs) {
    return sectionSchema;
  }

  return {
    ...sectionSchema,
    $defs: {
      ...rootSchema.$defs,
      ...sectionSchema.$defs,
    },
  };
};

const resolveFormSchemaTab = (
  section: JsonFormSection,
  schema: JsonFormObjectSchema,
): FormPageSchemaTab[] => {
  if (section.children?.length) {
    const parentSchema = schema.properties[section.property];

    if (!isObjectSchema(parentSchema)) {
      return [];
    }

    return section.children.flatMap((child) => {
      const childSchema = parentSchema.properties?.[child.property];

      if (!isObjectSchema(childSchema)) {
        return [];
      }

      return [
        {
          id: child.id,
          label: child.contentTitle ?? child.label,
          sidebarLabel: child.label,
          parentSectionId: section.id,
          dataPath: [section.property, child.property],
          schema: attachRootSchemaDefs(childSchema, schema),
        },
      ];
    });
  }

  const sectionSchema = schema.properties[section.property];

  if (!isObjectSchema(sectionSchema)) {
    return [];
  }

  return [
    {
      id: section.id,
      label: section.label,
      dataPath: [section.property],
      schema: attachRootSchemaDefs(sectionSchema, schema),
    },
  ];
};

export const getFormSchemaSidebarItems = (
  schema: JsonFormObjectSchema,
): FormSchemaSidebarItem[] => {
  const sections = schema["x-sections"];

  if (!sections) {
    return [];
  }

  return sections.map((section) => ({
    id: section.id,
    label: section.label,
    ...(section.children?.length
      ? {
          children: section.children.map((child) => ({
            id: child.id,
            label: child.label,
          })),
        }
      : {}),
  }));
};

export const getFormSchemaTabs = (
  schema: JsonFormObjectSchema,
  sectionIds?: string[],
): FormPageSchemaTab[] => {
  const allSections = schema["x-sections"];
  const sections = sectionIds
    ? sectionIds.flatMap((sectionId) => {
        const section = allSections?.find(({ id }) => id === sectionId);

        return section ? [section] : [];
      })
    : allSections;

  if (sections) {
    return sections.flatMap((section) => resolveFormSchemaTab(section, schema));
  }

  return Object.entries(schema.properties).flatMap(([property, propertySchema]) =>
    isObjectSchema(propertySchema)
      ? [
          {
            id: property,
            label: propertySchema.title ?? property,
            dataPath: [property],
            schema: propertySchema,
          },
        ]
      : [],
  );
};

export const getFieldTitle = (fieldName: string, field: JsonFormSchema): string =>
  field.title ?? field.label ?? fieldName;

const DYNAMIC_SECTION_INTERNAL_KEY = "_dynamicSectionId";

export const isEmptyFormValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0 || value.every(isEmptyFormValue);
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([key]) => key !== DYNAMIC_SECTION_INTERNAL_KEY);

    return entries.length === 0 || entries.every(([, entry]) => isEmptyFormValue(entry));
  }

  return false;
};

const isConditionalDependentVisible = (
  fieldName: string,
  objectSchema: JsonFormObjectSchema,
  objectValue: unknown,
): boolean => {
  const dependentRules = getConditionalRules(objectSchema).filter((rule) =>
    rule.dependentFieldNames.includes(fieldName),
  );

  if (dependentRules.length === 0) {
    return true;
  }

  if (!isPlainObject(objectValue)) {
    return false;
  }

  return dependentRules.some((rule) =>
    matchesConditionalShowIf(objectValue[rule.triggerFieldName], rule.showIf),
  );
};

const hasEmptyGenerationField = (schema: JsonFormSchema, value: unknown): boolean => {
  if (schema.type === "array") {
    if (!schema.items) {
      return false;
    }

    const items = Array.isArray(value) ? value : [];

    return items.some((item) => hasEmptyGenerationField(schema.items as JsonFormSchema, item));
  }

  if (schema.type === "object" && schema.properties) {
    const objectSchema = schema as JsonFormObjectSchema;

    return Object.entries(schema.properties).some(([fieldName, fieldSchema]) => {
      const fieldValue = isPlainObject(value) ? value[fieldName] : undefined;

      if (
        fieldSchema["x-needed-for-generation"] &&
        isEmptyFormValue(fieldValue) &&
        isConditionalDependentVisible(fieldName, objectSchema, value)
      ) {
        return true;
      }

      return hasEmptyGenerationField(fieldSchema, fieldValue);
    });
  }

  return false;
};

/** Whether a tab's schema has an `x-needed-for-generation` field (at any depth, including array items) that is currently empty in `values` — drives the plain section-level icon in the sidebar. */
export const getSectionNeedsGeneration = (
  schema: JsonFormObjectSchema,
  values: Record<string, unknown>,
): boolean => hasEmptyGenerationField(normalizeJsonFormSchema(schema), values);

export const cleanupFormDataForSave = (
  schema: JsonFormObjectSchema,
  data: Record<string, unknown>,
): Record<string, unknown> => {
  const normalizedSchema = normalizeJsonFormSchema(schema);
  const cleaned: Record<string, unknown> = {};

  for (const [key, field] of Object.entries(normalizedSchema.properties)) {
    if (isActionButtonField(field)) {
      continue;
    }

    const value = data[key];

    if (isEmptyFormValue(value)) {
      if (field.type === "array" && getFieldUiType(field) !== "dynamic-section") {
        cleaned[key] = value;
      }

      continue;
    }

    if (field.type === "array" && getFieldUiType(field) === "dynamic-section") {
      if (!Array.isArray(value)) {
        continue;
      }

      const itemSchema =
        field.items?.type === "object" && field.items.properties
          ? (field.items as JsonFormObjectSchema)
          : undefined;

      const cleanedItems = value
        .map((item) => {
          if (!isPlainObject(item)) {
            return item;
          }

          const { [DYNAMIC_SECTION_INTERNAL_KEY]: _internalId, ...itemFields } = item;

          return itemSchema ? cleanupFormDataForSave(itemSchema, itemFields) : itemFields;
        })
        .filter((item) => !isEmptyFormValue(item));

      if (cleanedItems.length > 0) {
        cleaned[key] = cleanedItems;
      }

      continue;
    }

    if (field.type === "object" && field.properties && isPlainObject(value)) {
      const nested = cleanupFormDataForSave(field as JsonFormObjectSchema, value);

      if (!isEmptyFormValue(nested)) {
        cleaned[key] = nested;
      }

      continue;
    }

    cleaned[key] = value;
  }

  return cleaned;
};

export const getFieldUiType = (field: JsonFormSchema): JsonFormUiType => {
  const explicitUiType = field["x-ui-type"] ?? field.uiType;

  if (explicitUiType) {
    return explicitUiType;
  }

  if (field.type === "array" && field.items?.enum) {
    return "checkbox-group";
  }

  if (field.enum) {
    return "select";
  }

  if (field.type === "boolean") {
    return "toggle";
  }

  if (field.type === "number" || field.type === "integer") {
    return "number";
  }

  return "text";
};

export const getFieldOptions = (field: JsonFormSchema): JsonFormOption[] => {
  const explicitOptions = field["x-options"] ?? field.options;

  if (explicitOptions) {
    return explicitOptions;
  }

  const values = field.enum ?? field.items?.enum ?? [];

  return values.map((value) => ({
    label: String(value),
    value,
  }));
};

export const getFieldPlaceholder = (field: JsonFormSchema): string | undefined =>
  field["x-placeholder"] ?? field.placeholder;

export const getFieldMask = (field: JsonFormSchema): string | undefined => field["x-mask"];

export const getFieldDisabledOptions = (field: JsonFormSchema): JsonFormOptionValue[] =>
  field["x-disabled-options"] ?? [];

export const getSectionInfo = (schema: JsonFormObjectSchema): string | undefined =>
  schema["x-section-info"];

export const getSectionLayout = (
  schema: JsonFormObjectSchema,
): "compact" | "default" => schema["x-section-layout"] ?? "default";

export const getSectionActions = (schema: JsonFormObjectSchema): JsonFormSectionAction[] =>
  schema["x-section-actions"] ?? [];
